'use client';

import React, { useId } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { FieldRow } from '@innovixx/react-form-fields';
import { useRouter } from 'next/navigation';
import type { CreateFormSubmissionMutation, CreateFormSubmissionMutationVariables, Form } from '../../graphql/generated/schema';
import { AppCheckboxInput, AppNumberInput, AppSelectInput, AppTextAreaInput, AppTextInput, AppToggleInput } from '../formFields';
import { RichText } from '../RichText';
import { Button } from '../Button';
import styles from './styles.module.scss';
import { cmsClient } from '../../graphql';
import { CREATE_FORM_SUBMISSION } from '../../graphql/mutation/createFormSubmission';
import { useUtmParams } from '../../providers';
import { GovUkNetZeroBadge, SSLCertificateBadge } from '../graphics';
import colors from '../../styles/colors/index.module.scss';

interface Props {
  isDuplicatedForm?: boolean;
  form: Form;
  className?: string;
  submitButtonClassName?: string;
}

export const PayloadForm: React.FC<Props> = ({ isDuplicatedForm = false, form, className, submitButtonClassName }) => {
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { control, handleSubmit } = useForm();
  const router = useRouter();
  const { values: utmParams } = useUtmParams();

  const submitForm = async (data: FieldValues) => {
    setLoading(true);
    setSubmitted(false);
    setError(null);

    const dataToSend = [
      ...Object.entries(data).map(([name, value]) => ({
        field: name,
        value: `${value}` || '',
      })),
      ...utmParams ? Object.entries(utmParams).map(([name, value]) => ({
        field: name,
        value: `${value}` || '',
      })) : [],
    ];

    const res = await cmsClient.request<CreateFormSubmissionMutation, CreateFormSubmissionMutationVariables>(CREATE_FORM_SUBMISSION, {
      data: {
        submissionData: dataToSend,
        form: form.id,
      },
    }).catch(() => {
      setError('There was an error submitting the form. Please try again later.');
      setLoading(false);
      return Promise.reject();
    }).then((response) => response);

    if (res.createFormSubmission?.id) {
      if (form.confirmationType === 'redirect' && form.redirect?.url) {
        router.push(form.redirect?.url);
      } else {
        setSubmitted(true);
      }
    }
    setLoading(false);
  };
  // const submitFormRef = useRef(submitForm);

  // React.useEffect(() => {
  //   if (loading) return;
  //   const onlyTogglesAndSelects = form.fields?.every((field) => field.blockType === 'toggle' || field.blockType === 'select');
  //   if (onlyTogglesAndSelects && Object.keys(values).length === form.fields?.length && Object.keys(values).every((key) => values[key] !== null)) {
  //     handleSubmit(submitFormRef.current)();
  //   }
  // }, [control, form.fields, values, submitFormRef, handleSubmit, loading]);

  const uniqueFormId = useId();

  return (
    <form
      className={className}
      onSubmit={handleSubmit(submitForm)}
    >
      {
        submitted && (
          <RichText
            data={form.confirmationMessage}
          />
        )
      }
      {
        error && (
          <p>
            {error}
          </p>
        )
      }
      {!submitted && (
        <React.Fragment>
          <FieldRow>
            {form.fields?.map((item) => {
              const field: {
                name: string;
                label: string;
                required: boolean;
                width: number;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                message: any
                options: {
                  label: string;
                  value: string;
                }[]
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                defaultValue?: any;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } = item as any;
              switch (true) {
                case (item.blockType === 'text'):
                  return (
                    <AppTextInput
                      control={control}
                      name={field.name}
                      label={`${field.label}`}
                      required={Boolean(field.required)}
                      width={field.width || 100}
                      defaultValue={field.defaultValue}
                    />
                  );
                case (item.blockType === 'textarea'):
                  return (
                    <AppTextAreaInput
                      control={control}
                      name={field.name}
                      label={`${field.label}`}
                      required={Boolean(field.required)}
                      width={field.width || 100}
                      defaultValue={field.defaultValue}
                    />
                  );
                case (item.blockType === 'select'):
                  return (
                    <AppSelectInput
                      control={control}
                      name={field.name}
                      label={`${field.label}`}
                      required={Boolean(field.required)}
                      width={field.width || 100}
                      options={field.options}
                      defaultValue={field.defaultValue}
                    />
                  );
                case (item.blockType === 'toggle'):
                  return (
                    <AppToggleInput
                      control={control}
                      name={isDuplicatedForm ? `${field.name}-${uniqueFormId}` : field.name}
                      label={`${field.label}`}
                      required={Boolean(field.required)}
                      width={field.width || 100}
                      options={field.options}
                      defaultValue={field.defaultValue}
                    />
                  );
                case (item.blockType === 'email'):
                  return (
                    <AppTextInput
                      rules={{
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                          message: 'Invalid email address',
                        },
                      }}
                      control={control}
                      name={field.name}
                      label={`${field.label}`}
                      required={Boolean(field.required)}
                      width={field.width || 100}
                      defaultValue={field.defaultValue}
                    />
                  );
                case (item.blockType === 'checkbox'):
                  return (
                    <AppCheckboxInput
                      control={control}
                      name={field.name}
                      label={`${field.label}`}
                      required={Boolean(field.required)}
                      width={field.width || 100}
                      defaultChecked={Boolean(field.defaultValue)}
                    />
                  );
                case (item.blockType === 'number'):
                  return (
                    <AppNumberInput
                      control={control}
                      name={field.name}
                      label={`${field.label}`}
                      required={Boolean(field.required)}
                      width={field.width || 100}
                      defaultValue={field.defaultValue}
                    />
                  );
                case (item.blockType === 'message'):
                  return (
                    <div style={{
                      flex: '0 0 calc(100% - 20px)',
                      flexGrow: 1,
                    }}
                    >
                      <RichText
                        data={field.message}
                      />
                    </div>

                  );
                default:
                  return null;
              }
            })}
          </FieldRow>
          <div className={styles.submitButtonContainer}>
            <Button
              className={submitButtonClassName}
              htmlType="submit"
              loading={loading}
            >
              {form.submitButtonLabel}
            </Button>
          </div>
          <span className={styles.disclaimerText}>
            By submitting this form, you agree to our
            {' '}
            <a
              href="/privacy-policy"
              target="_blank"
            >
              Privacy Policy
            </a>
          </span>
          <div className={styles.footerContainer}>
            <GovUkNetZeroBadge
              width={64}
              fill={colors.white}
              className={styles.netZeroBadge}
            />
            <SSLCertificateBadge
              width={64}
            />
          </div>
        </React.Fragment>
      )}

    </form>
  );
};
